/**
 * Filter model of the accounts list — plain data, no React, so the counters,
 * the Status select and the table can't drift apart: they all read through
 * `matchesFilters`, and every number shown is what clicking it would list.
 *
 * The state lives in the URL (`/accounts?status=active&proxy=<id>`), which is
 * how the Proxies page and the dashboard link straight into a filtered list.
 */

import {
  type AccountIssue,
  type AccountRole,
  type AccountSummary,
  type DerivedStatus,
  ACCOUNT_ISSUES,
  deriveAccountIssue,
  deriveAccountStatus,
} from "@/lib/accounts";

/** Select value for "no filter" — Radix Select can't take an empty string. */
export const ALL = "__all__";
/** Select value for "the field is empty" (no group / no geo / no proxy). */
export const NONE = "__none__";

/** Every status a row's badge can show, in the Status filter's order. */
export const DERIVED_STATUSES: DerivedStatus[] = [
  "active",
  "warming_up",
  "flood_wait",
  "banned",
  "paused",
  "pending_verify",
  "frozen",
  "auth_required",
  "proxy_dead",
];
export const ROLES: AccountRole[] = ["inviter", "scraper", "both", "demoted_scraper"];
export const CHECKED_BANDS = ["day", "week", "older", "never"] as const;
export const TWOFA_STATES = ["set", "unset"] as const;
export const AGE_BANDS = ["lt7", "7to30", "30to180", "gt180"] as const;

export interface AccountFilters {
  q: string;
  status: string;
  /** An AccountIssue, "any" (all issues), or ALL (no issue filter). */
  issue: string;
  group: string;
  spam: string;
  country: string;
  health: string;
  // "More filters"
  role: string;
  proxy: string;
  checked: string;
  twofa: string;
  age: string;
}

export const EMPTY_FILTERS: AccountFilters = {
  q: "",
  status: ALL,
  issue: ALL,
  group: ALL,
  spam: ALL,
  country: ALL,
  health: ALL,
  role: ALL,
  proxy: ALL,
  checked: ALL,
  twofa: ALL,
  age: ALL,
};

/** The filters tucked under "More filters". */
export const MORE_FILTER_KEYS: (keyof AccountFilters)[] = [
  "role",
  "proxy",
  "checked",
  "twofa",
  "age",
];

const KEYS = Object.keys(EMPTY_FILTERS) as (keyof AccountFilters)[];

// A hand-edited or stale link must not put an unknown value into a filter:
// it would match nothing and show a raw translation key in its chip.
const ALLOWED: Partial<Record<keyof AccountFilters, readonly string[]>> = {
  status: DERIVED_STATUSES,
  issue: ["any", ...ACCOUNT_ISSUES],
  spam: ["clean", "limited", "unknown"],
  health: ["high", "mid", "low"],
  role: ROLES,
  checked: CHECKED_BANDS,
  twofa: TWOFA_STATES,
  age: AGE_BANDS,
};

function isSet(f: AccountFilters, k: keyof AccountFilters): boolean {
  return k === "q" ? f.q.trim() !== "" : f[k] !== EMPTY_FILTERS[k];
}

export function filtersFromSearch(search: string): AccountFilters {
  const params = new URLSearchParams(search);
  const f = { ...EMPTY_FILTERS };
  for (const k of KEYS) {
    const v = params.get(k);
    if (!v) continue;
    const allowed = ALLOWED[k];
    if (allowed && !allowed.includes(v)) continue;
    f[k] = v;
  }
  return f;
}

export function filtersToSearch(f: AccountFilters): string {
  const params = new URLSearchParams();
  for (const k of KEYS) {
    if (isSet(f, k)) params.set(k, f[k]);
  }
  return params.toString();
}

/** Filters that narrow the list, the search box included. */
export function activeFilterKeys(f: AccountFilters): (keyof AccountFilters)[] {
  return KEYS.filter((k) => isSet(f, k));
}

/** The account number a query names — "#064", "064" or "64" — else null. */
export function queryAccountNo(raw: string): number | null {
  const m = /^#?0*(\d+)$/.exec(raw.trim());
  return m ? Number(m[1]) : null;
}

/**
 * Free-text search. "#064" means that account number and nothing else; a bare
 * number also matches the number, plus anything that contains it (TG id,
 * phone, a username like demo_acc_64).
 */
export function matchesQuery(a: AccountSummary, raw: string): boolean {
  const q = raw.trim().toLowerCase();
  if (!q) return true;
  const no = queryAccountNo(q);
  if (q.startsWith("#")) return no !== null && a.seq_no === no;
  if (no !== null && a.seq_no === no) return true;
  const needle = q.replace(/^@/, "");
  const haystack = [
    a.tg_username,
    a.display_name,
    a.first_name,
    a.last_name,
    a.tg_user_id?.toString(),
    a.source_label,
    a.proxy_label,
    a.phone,
  ];
  if (haystack.some((v) => v?.toLowerCase().includes(needle))) return true;
  // The internal UUID by its start only: "64" would otherwise hit about one
  // account in ten somewhere inside its random hex.
  if (a.id.toLowerCase().startsWith(needle)) return true;
  // Phones are stored as +79991234567; let "999 123" find them too.
  const digits = needle.replace(/[\s()+-]/g, "");
  return /^\d+$/.test(digits) && (a.phone ?? "").replace(/\D/g, "").includes(digits);
}

const MS_PER_DAY = 86_400_000;

function matchesHealth(a: AccountSummary, band: string): boolean {
  const h = a.health_score ?? 0;
  if (band === "high") return h >= 70;
  if (band === "mid") return h >= 40 && h < 70;
  if (band === "low") return h < 40;
  return true;
}

function matchesChecked(a: AccountSummary, band: string, now: number): boolean {
  if (band === ALL) return true;
  if (a.last_check_at === null) return band === "never";
  const days = (now - new Date(a.last_check_at).getTime()) / MS_PER_DAY;
  if (band === "day") return days < 1;
  if (band === "week") return days >= 1 && days < 7;
  if (band === "older") return days >= 7;
  return false;
}

/**
 * Same rule as the health score's age factor (app/core/account_score.py): the
 * seller's stated age when we have it, else the time since import.
 */
export function accountAgeDays(a: AccountSummary, now: number): number {
  if (a.seller_account_age_days !== null) return Math.max(0, a.seller_account_age_days);
  return Math.max(0, Math.floor((now - new Date(a.created_at).getTime()) / MS_PER_DAY));
}

function matchesAge(a: AccountSummary, band: string, now: number): boolean {
  if (band === ALL) return true;
  const d = accountAgeDays(a, now);
  if (band === "lt7") return d < 7;
  if (band === "7to30") return d >= 7 && d < 30;
  if (band === "30to180") return d >= 30 && d < 180;
  if (band === "gt180") return d >= 180;
  return false;
}

function matchesOptional(value: string | null, filter: string): boolean {
  if (filter === ALL) return true;
  if (filter === NONE) return !value;
  return value === filter;
}

/** Everything except status and issue — the base the counters are taken over. */
export function matchesBaseFilters(
  a: AccountSummary,
  f: AccountFilters,
  now: number = Date.now(),
): boolean {
  return (
    matchesQuery(a, f.q) &&
    matchesOptional(a.source_label, f.group) &&
    (f.spam === ALL || (a.spam_state ?? "unknown") === f.spam) &&
    matchesOptional(a.country_code, f.country) &&
    matchesHealth(a, f.health) &&
    (f.role === ALL || a.role === f.role) &&
    matchesOptional(a.proxy_id, f.proxy) &&
    matchesChecked(a, f.checked, now) &&
    (f.twofa === ALL || (a.tg_2fa_locked_at !== null) === (f.twofa === "set")) &&
    matchesAge(a, f.age, now)
  );
}

export function matchesFilters(
  a: AccountSummary,
  f: AccountFilters,
  now: number = Date.now(),
): boolean {
  if (!matchesBaseFilters(a, f, now)) return false;
  if (f.status !== ALL && deriveAccountStatus(a) !== f.status) return false;
  if (f.issue !== ALL) {
    const issue = deriveAccountIssue(a);
    if (issue === null) return false;
    if (f.issue !== "any" && issue !== f.issue) return false;
  }
  return true;
}

export interface FleetCounts {
  total: number;
  byStatus: Partial<Record<DerivedStatus, number>>;
  issues: number;
  byIssue: Record<AccountIssue, number>;
}

export function countFleet(rows: AccountSummary[]): FleetCounts {
  const byStatus: Partial<Record<DerivedStatus, number>> = {};
  const byIssue = Object.fromEntries(ACCOUNT_ISSUES.map((i) => [i, 0])) as Record<
    AccountIssue,
    number
  >;
  let issues = 0;
  for (const a of rows) {
    const s = deriveAccountStatus(a);
    byStatus[s] = (byStatus[s] ?? 0) + 1;
    const issue = deriveAccountIssue(a);
    if (issue !== null) {
      byIssue[issue] += 1;
      issues += 1;
    }
  }
  return { total: rows.length, byStatus, issues, byIssue };
}
